import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ToggleLeft, ToggleRight } from 'lucide-react';
import featureToggleService from '../services/featureToggleService';
import getTypesService from '../services/getTypes';

const FeatureToggles = () => {
  const [types, setTypes] = useState([]);
  const [featureToggles, setFeatureToggles] = useState({
    documentUpload: true,
    disabledInsuranceTypes: [],
  });
  const [togglesLoading, setTogglesLoading] = useState(true);
  const [savingToggles, setSavingToggles] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTypes();
    fetchFeatureToggles();
  }, []);

  const fetchTypes = async () => {
    try {
      const types = await getTypesService.getTypes();
      setTypes(types);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      setTogglesLoading(true);
      const toggles = await featureToggleService.getFeatureToggles();
      if (toggles) {
        setFeatureToggles({
          documentUpload:
            toggles.documentUpload !== undefined
              ? toggles.documentUpload
              : true,
          disabledInsuranceTypes: toggles.disabledInsuranceTypes || [],
        });
      }
    } catch (error) {
      console.error('Failed to fetch feature toggles:', error.message);
    } finally {
      setTogglesLoading(false);
    }
  };

  const handleToggleDocumentUpload = async () => {
    const newValue = !featureToggles.documentUpload;
    setSavingToggles(true);
    try {
      const updated = await featureToggleService.updateFeatureToggles({
        documentUpload: newValue,
        disabledInsuranceTypes: featureToggles.disabledInsuranceTypes,
      });
      if (updated) {
        setFeatureToggles({
          documentUpload: updated.documentUpload,
          disabledInsuranceTypes: updated.disabledInsuranceTypes || [],
        });
      }
    } catch (error) {
      console.error('Failed to toggle document upload:', error.message);
    } finally {
      setSavingToggles(false);
    }
  };

  const handleToggleInsuranceType = async (typeName) => {
    const isCurrentlyDisabled =
      featureToggles.disabledInsuranceTypes.includes(typeName);
    const newDisabledTypes = isCurrentlyDisabled
      ? featureToggles.disabledInsuranceTypes.filter((t) => t !== typeName)
      : [...featureToggles.disabledInsuranceTypes, typeName];
    setSavingToggles(true);
    try {
      const updated = await featureToggleService.updateFeatureToggles({
        documentUpload: featureToggles.documentUpload,
        disabledInsuranceTypes: newDisabledTypes,
      });
      if (updated) {
        setFeatureToggles({
          documentUpload: updated.documentUpload,
          disabledInsuranceTypes: updated.disabledInsuranceTypes || [],
        });
      }
    } catch (error) {
      console.error('Failed to toggle insurance type:', error.message);
    } finally {
      setSavingToggles(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 sm:px-6 py-4 max-w-5xl mx-auto">
      {/* Header Section */}
      <div className="mb-5">
        <button
          onClick={() => navigate(-1)}
          className="
            inline-flex
            items-center
            gap-2
            px-3
            py-1.5
            rounded-md
            bg-gray-200
            text-gray-700
            text-sm
            font-medium
            hover:bg-gray-300
            transition
          "
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <h1 className="mt-2 text-xl sm:text-2xl font-semibold text-gray-800 flex items-center gap-2">
          <ToggleRight size={24} className="text-blue-600" />
          Feature Toggles
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Control feature visibility across the platform
        </p>
      </div>

      {togglesLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500 text-sm">
            Loading toggles...
          </span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Document Upload Feature Toggle */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Feature Controls
            </h2>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                    Document Upload (Fill Manually / Upload JPG)
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    When turned OFF, the &ldquo;Filling Manually / Upload
                    JPG&rdquo; button will be hidden in Buy Insurance and New
                    Listing pages. Form will work in manual-only mode.
                  </p>
                </div>
                <button
                  onClick={handleToggleDocumentUpload}
                  disabled={savingToggles}
                  className={`flex-shrink-0 relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 outline-none ${
                    featureToggles.documentUpload
                      ? 'bg-green-500'
                      : 'bg-red-400'
                  } ${
                    savingToggles
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                      featureToggles.documentUpload
                        ? 'translate-x-8'
                        : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="mt-3">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    featureToggles.documentUpload
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {featureToggles.documentUpload ? (
                    <>
                      <ToggleRight size={14} /> Enabled
                    </>
                  ) : (
                    <>
                      <ToggleLeft size={14} /> Disabled
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Insurance Type Toggles */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-1">
              Insurance Type Visibility
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mb-4">
              Turn OFF any insurance type to hide it from the Type dropdown in
              Buy Insurance and New Listing pages.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {types &&
                types.map((typeName) => {
                  const isEnabled =
                    !featureToggles.disabledInsuranceTypes.includes(typeName);
                  return (
                    <div
                      key={typeName}
                      className={`flex items-center justify-between p-3 sm:p-4 rounded-lg border transition-all duration-200 ${
                        isEnabled
                          ? 'bg-white border-gray-200 hover:border-green-300 hover:shadow-sm'
                          : 'bg-gray-50 border-gray-200 opacity-70'
                      }`}
                    >
                      <span
                        className={`text-sm font-medium ${
                          isEnabled
                            ? 'text-gray-800'
                            : 'text-gray-400 line-through'
                        }`}
                      >
                        {typeName}
                      </span>
                      <button
                        onClick={() => handleToggleInsuranceType(typeName)}
                        disabled={savingToggles}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 outline-none ${
                          isEnabled ? 'bg-green-500' : 'bg-red-400'
                        } ${
                          savingToggles
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${
                            isEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
            </div>

            {/* Summary of disabled types */}
            {featureToggles.disabledInsuranceTypes.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-600">
                    {featureToggles.disabledInsuranceTypes.length}
                  </span>{' '}
                  insurance type
                  {featureToggles.disabledInsuranceTypes.length > 1
                    ? 's'
                    : ''}{' '}
                  currently disabled:{' '}
                  <span className="text-red-500 font-medium">
                    {featureToggles.disabledInsuranceTypes.join(', ')}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureToggles;
